import type { ApiException } from '@/common/error';
import { mapTemporaryToDto } from '@/store/contracts/contracts.mappers';
import { formatEventDateTime } from '@/utils/date';

import type {
  CreateInvitation,
  EventInviteAnswer,
  EventInviteAnswerDto,
  EventInviteAnswerState,
  EventInviteAnswerStateDto,
  EventInviteStatus,
  EventInviteStatusDto,
  InvitationDto,
  InviteCode,
  InviteCodeDto,
  JoinedVessel,
  JoinInviteCodeDto,
  SeatAvailability,
  SeatAvailabilityDto,
} from './invites.types';

export const mapInviteToDto = ({
  personId,
  role,
  serviceId,
  serviceType,
  roleName,
  vesselName,
  name,
  event,
  message,
  temporary,
  additionalGuests,
}: CreateInvitation): InvitationDto => ({
  person_id: personId,
  role,
  service_id: serviceId,
  service_type: serviceType,
  role_name: roleName,
  service_name: vesselName,
  name,
  event,
  message,
  temporary: mapTemporaryToDto(temporary),
  additional_guests: additionalGuests,
});

/** What an event invitation's link lasts: the inviter's pick, or the default rule. */
export const inviteExpiryNote = (expiresAt?: string | null): string =>
  expiresAt
    ? `The link expires on ${formatEventDateTime(expiresAt)}.`
    : 'The link expires when the event starts, or 72 hours after it’s created once the event is underway.';

/** Why a picked invitation deadline can't be used: one already past, or one after the event ends. */
export const inviteExpiryError = (
  expiresAt?: string | null,
  eventEndsAt?: string | Date | null,
): string | undefined => {
  if (!expiresAt) return undefined;
  const at = Date.parse(expiresAt);
  if (at <= Date.now()) return 'Please pick a time in the future.';
  if (eventEndsAt && at > new Date(eventEndsAt).getTime()) {
    return 'Please pick a time before the event ends.';
  }
  return undefined;
};

export const GUEST_COUNT_ERRORS = {
  empty: 'Please enter how many guests they bring, or 0 for none.',
  negative: 'The number of guests can’t be below 0.',
  notWhole: 'Please enter a whole number, like 0, 1 or 2.',
} as const;

/** A typed guest count, or undefined unless it is a whole number of 0 or more. */
export const parseGuestCount = (value: string): number | undefined =>
  /^\d+$/.test(value.trim()) ? Number(value) : undefined;

/** Why a typed guest count can't be used, or undefined when it can. */
export const guestCountError = (value: string): string | undefined => {
  const typed = value.trim();
  if (!typed) return GUEST_COUNT_ERRORS.empty;
  if (parseGuestCount(typed) !== undefined) return undefined;
  const n = Number(typed);
  return Number.isFinite(n) && n < 0 ? GUEST_COUNT_ERRORS.negative : GUEST_COUNT_ERRORS.notWhole;
};

export const mapEventInviteAnswerState = ({
  accepted,
  answered_at,
  additional_guests,
  host_name,
  vessel_name,
}: EventInviteAnswerStateDto): EventInviteAnswerState => ({
  accepted,
  answeredAt: answered_at,
  additionalGuests: additional_guests ?? 0,
  hostName: host_name ?? '',
  vesselName: vessel_name ?? '',
});

export const mapEventInviteStatus = ({
  expires_at,
  expired,
  reason,
  additional_guests,
  host_name,
  vessel_name,
}: EventInviteStatusDto): EventInviteStatus => ({
  expiresAt: expires_at,
  expired,
  reason,
  additionalGuests: additional_guests ?? 0,
  hostName: host_name ?? '',
  vesselName: vessel_name ?? '',
});

export const mapEventInviteAnswer = ({
  id,
  invite_id,
  creator_id,
  contact,
  name,
  accepted,
  decline_reason,
  created_at,
  updated_at,
  additional_guests,
}: EventInviteAnswerDto): EventInviteAnswer => ({
  id,
  inviteId: invite_id,
  creatorId: creator_id,
  contact,
  name,
  accepted,
  declineReason: decline_reason,
  answeredAt: updated_at ?? created_at,
  additionalGuests: additional_guests ?? 0,
});

export const mapInviteCode = ({
  id,
  code,
  service_id,
  role,
  seat_available,
  seat_auto_charge,
  expires_at,
}: InviteCodeDto): InviteCode => ({
  id,
  code,
  serviceId: service_id,
  role,
  expiresAt: expires_at,
  seatAvailable: seat_available,
  seatAutoCharge: seat_auto_charge,
});

export const mapSeatAvailability = ({
  pool,
  available,
  auto_charge,
}: SeatAvailabilityDto): SeatAvailability => ({ pool, available, autoCharge: auto_charge });

/**
 * What the invite form says about the seat the invitee takes, or nothing when a free one is
 * waiting (or the plan pays for none). A temporary hire's bought seat goes off the plan again when
 * the contract ends.
 */
export const seatAvailabilityWarning = (
  seat: SeatAvailability | undefined,
  temporary = false,
): string | undefined => {
  if (!seat?.pool || seat.available) return undefined;

  if (!seat.autoCharge) {
    return `There's no free ${seat.pool} seat and this plan can't add one, so they'll join with Harbor access until a seat is freed.`;
  }

  return `There's no free ${seat.pool} seat. When they join, one more is added to the plan and charged to its payer${temporary ? ', then taken off again when the contract ends' : ''}. Free a seat first to avoid the charge.`;
};

export const mapJoinedVessel = ({
  service_id,
  service_name,
  role,
}: JoinInviteCodeDto): JoinedVessel => ({
  serviceId: service_id,
  serviceName: service_name || undefined,
  role,
});

/**
 * What to say when a code does not let somebody in. The people service answers a mistyped,
 * taken, withdrawn or lapsed code with the same 404 on purpose, so the copy does not tell them
 * apart either.
 */
export const inviteCodeErrorMessage = (error: unknown): string => {
  const { domain, type } = (error as Partial<ApiException> | null) ?? {};
  if (domain === 'INVITES' && type === 'NOT_FOUND')
    return 'That code is wrong or has expired. Please ask for a new one.';
  if (type === 'ALREADY_ON_VESSEL')
    return 'You’re already on this vessel. Please open it from your dashboard.';
  return 'Couldn’t join the vessel. Please try again.';
};
